import { ListsInclusions } from '@app/data/clients/rbServerSideClient/queries/GetListInclusions/types';
import { ProcessedSearchResultItem } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import Modal from '@redbubble/design-system/src/Modal';
import Sheets, { Sheet } from '@redbubble/design-system/src/Sheets';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { NodeItem } from 'pages/shop/types';
import { ListsButtonInventoryItem, UserLists } from 'types/shop-bff/types';
import { Spinner } from '../Spinner';
import { CreateListForm } from './CreateListForm';
import ListItem from './ListItem';
import styles from './ListsModal.module.scss';

const messages = defineMessages({
  modalAccessibleTitle: {
    defaultMessage: 'Lists',
  },
  chooseList: {
    defaultMessage: 'Choose a list',
  },
  done: {
    defaultMessage: 'Done',
  },
  createList: {
    defaultMessage: 'Create new list',
  },
});

interface ListsModalProps {
  open: boolean;
  inventoryItem: ListsButtonInventoryItem | ProcessedSearchResultItem;
  closeModal: () => void;
  handleCheckbox: (
    listId: string,
    inventoryItemId: string,
    isInList: boolean
  ) => void;
  handleSubmitForm: (name: string) => Promise<NodeItem | null | undefined>;
  listsInclusions?: ListsInclusions | null;
  userLists?: UserLists | null;
  loading: boolean;
}

export const ListsModal: React.FC<ListsModalProps> = ({
  open,
  inventoryItem,
  closeModal,
  handleCheckbox,
  handleSubmitForm,
  listsInclusions,
  userLists,
  loading,
}) => {
  const intl = useIntl();

  return (
    <Modal
      accessibleTitle={intl.formatMessage(messages.modalAccessibleTitle)}
      open={open || false}
      onCloseRequested={() => closeModal()}
      getApplicationNode={() => document.getElementById('root')}
      profile={constants.MOBILE}
      data-testid="modal"
      size={constants.SMALL}
    >
      {({ close }) => (
        <Box className={styles.listsModal}>
          <Sheets
            intl={intl}
            title={intl.formatMessage(messages.chooseList)}
            rightAction={
              <Button
                onClick={() => close()}
                size={constants.SMALL}
                intent={constants.PRIMARY}
              >
                {intl.formatMessage(messages.done)}
              </Button>
            }
            sheets={{
              createList: (
                <Sheet title={intl.formatMessage(messages.createList)}>
                  {({ goBack }) => (
                    <Box padding="m">
                      <CreateListForm
                        onClose={goBack}
                        handleSubmitForm={handleSubmitForm}
                      />
                    </Box>
                  )}
                </Sheet>
              ),
            }}
          >
            {({ openSheet }) => (
              <Box padding="m">
                <Box paddingBottom="m" display="block">
                  <Button
                    onClick={() => openSheet('createList')}
                    size={constants.SMALL}
                    intent={constants.PRIMARY}
                  >
                    {intl.formatMessage(messages.createList)}
                  </Button>
                </Box>
                <Box>
                  {loading ? (
                    <Spinner />
                  ) : (
                    userLists?.lists?.nodes?.map((node) => (
                      <ListItem
                        node={node}
                        listsInclusions={listsInclusions}
                        inventoryItem={inventoryItem}
                        handleCheckbox={handleCheckbox}
                      />
                    ))
                  )}
                </Box>
              </Box>
            )}
          </Sheets>
        </Box>
      )}
    </Modal>
  );
};
